import React, { useState } from 'react';
import { CheckCircle, XCircle, Clock, Search } from 'lucide-react';
import { useQuery, getPendingUsers, approveUser, rejectUser } from 'wasp/client/operations';
import { Button } from '../../../components/ui/button';
import { toast } from '../../../hooks/use-toast';
import useDebounce from '../../../client/hooks/useDebounce';

type PendingUser = {
  id: string;
  email: string | null;
  username: string | null;
  fullName: string | null;
  authProvider: string | null;
  createdAt: Date;
  platformRole: string;
};

const UserApprovalsPage = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [rejectingId, setRejectingId] = useState<string | null>(null);
  const [rejectReason, setRejectReason] = useState('');

  const debouncedSearch = useDebounce(searchTerm, 300);

  const { data: pendingUsers = [], isLoading, refetch } = useQuery(getPendingUsers);

  const filtered = debouncedSearch
    ? (pendingUsers as PendingUser[]).filter(
        (u: PendingUser) =>
          u.email?.toLowerCase().includes(debouncedSearch.toLowerCase()) ||
          u.fullName?.toLowerCase().includes(debouncedSearch.toLowerCase())
      )
    : (pendingUsers as PendingUser[]);

  const handleApprove = async (userId: string) => {
    try {
      await approveUser({ userId });
      await refetch();
      toast({ title: 'User approved', description: 'The user has been notified by email.' });
    } catch (error: any) {
      toast({ title: 'Error', description: error.message || 'Failed to approve user', variant: 'destructive' });
    }
  };

  const handleRejectSubmit = async (userId: string) => {
    try {
      await rejectUser({ userId, reason: rejectReason || undefined });
      await refetch();
      setRejectingId(null);
      setRejectReason('');
      toast({ title: 'User rejected', description: 'The user has been notified by email.' });
    } catch (error: any) {
      toast({ title: 'Error', description: error.message || 'Failed to reject user', variant: 'destructive' });
    }
  };

  return (
    <div className="w-full">
      {/* Fixed Header */}
      <div className="fixed top-[var(--header-height)] left-[var(--sidebar-width)] right-0 z-30 bg-white dark:bg-sidebar-active transition-colors border-b border-gray-200 dark:border-gray-700">
        <div className="px-4 py-3">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900 dark:text-white">User Approvals</h1>
              <p className="text-sm text-gray-600 dark:text-gray-300 mt-1">
                Review and approve new user registrations.
              </p>
            </div>
            <div className="flex items-center gap-3">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search by email or name…"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-9 pr-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 w-64"
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Spacer */}
      <div className="h-20" />

      {/* Content */}
      <div className="px-4 py-4">
        {isLoading ? (
          <p className="text-sm text-gray-500 py-8 text-center">Loading pending users…</p>
        ) : filtered.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-center">
            <CheckCircle className="w-12 h-12 text-green-400 mb-3" />
            <p className="text-lg font-medium text-gray-700">All clear!</p>
            <p className="text-sm text-gray-500 mt-1">No pending user registrations.</p>
          </div>
        ) : (
          <div className="overflow-x-auto rounded-md border border-gray-200 dark:border-gray-700">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700 text-sm">
              <thead className="bg-gray-50 dark:bg-gray-800">
                <tr>
                  <th className="px-4 py-3 text-left font-medium text-gray-500 dark:text-gray-400">User</th>
                  <th className="px-4 py-3 text-left font-medium text-gray-500 dark:text-gray-400">Provider</th>
                  <th className="px-4 py-3 text-left font-medium text-gray-500 dark:text-gray-400">Registered</th>
                  <th className="px-4 py-3 text-left font-medium text-gray-500 dark:text-gray-400">Status</th>
                  <th className="px-4 py-3 text-right font-medium text-gray-500 dark:text-gray-400">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-700 bg-white dark:bg-gray-900">
                {filtered.map((user: PendingUser) => (
                  <React.Fragment key={user.id}>
                    <tr className="hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors">
                      <td className="px-4 py-3">
                        <div className="font-medium text-gray-900 dark:text-white">{user.email}</div>
                        {user.fullName && (
                          <div className="text-xs text-gray-500">{user.fullName}</div>
                        )}
                      </td>
                      <td className="px-4 py-3 text-gray-600 dark:text-gray-400 capitalize">
                        {user.authProvider || '—'}
                      </td>
                      <td className="px-4 py-3 text-gray-600 dark:text-gray-400">
                        {new Date(user.createdAt).toLocaleDateString('en-CA', {
                          year: 'numeric',
                          month: 'short',
                          day: 'numeric',
                        })}
                      </td>
                      <td className="px-4 py-3">
                        <span className="inline-flex items-center gap-1 text-xs font-medium text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-2 py-0.5">
                          <Clock className="w-3 h-3" />
                          Pending
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="flex items-center justify-end gap-2">
                          <Button
                            size="sm"
                            variant="default"
                            className="bg-green-600 hover:bg-green-700 text-white text-xs"
                            onClick={() => handleApprove(user.id)}
                          >
                            <CheckCircle className="w-3.5 h-3.5 mr-1" />
                            Approve
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            className="border-red-300 text-red-600 hover:bg-red-50 text-xs"
                            onClick={() => {
                              setRejectingId(user.id);
                              setRejectReason('');
                            }}
                          >
                            <XCircle className="w-3.5 h-3.5 mr-1" />
                            Reject
                          </Button>
                        </div>
                      </td>
                    </tr>
                    {/* Inline reject reason form */}
                    {rejectingId === user.id && (
                      <tr className="bg-red-50 dark:bg-red-950">
                        <td colSpan={5} className="px-4 py-3">
                          <div className="flex items-center gap-3">
                            <input
                              type="text"
                              placeholder="Optional reason for rejection…"
                              value={rejectReason}
                              onChange={(e) => setRejectReason(e.target.value)}
                              className="flex-1 text-sm border border-red-300 rounded-md px-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-red-400"
                              autoFocus
                            />
                            <Button
                              size="sm"
                              className="bg-red-600 hover:bg-red-700 text-white text-xs"
                              onClick={() => handleRejectSubmit(user.id)}
                            >
                              Confirm Reject
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              className="text-xs"
                              onClick={() => { setRejectingId(null); setRejectReason(''); }}
                            >
                              Cancel
                            </Button>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default UserApprovalsPage;



